import { Button, Divider, IconButton, Popover } from '@material-ui/core';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import PersonAddDisabledOutlinedIcon from '@material-ui/icons/PersonAddDisabledOutlined';
import { ArticlePropertiesLabelledPropertiesX } from 'app/main/inventoryManagement/inventoryItems/detail/articleProperties/ArticlePropertiesLabelledPropertiesX';
import { ArticlePropertiesDialog } from 'app/main/inventoryManagement/inventoryItems/forms/articleProperties/ArticlePropertiesDialog';
import { useLocalizedArticlePropertyKeys } from 'app/main/inventoryManagement/productCatalog/articlePropertyKeys/context/useLocalizedArticlePropertyKeys';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CollapseX } from 'framework/components/CollapseX';
import { IDraggableItem } from 'framework/components/dnd/IDraggableItem';
import { SelectFieldFromRecord } from 'framework/components/select/SelectFieldFromRecord';
import { IntegerTextField } from 'framework/components/textFields/IntegerTextField';
import { isNullOrUndefined } from 'framework/utils/isNullOrUndefined';
import { Orientation } from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import React, { useContext, useState } from 'react';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { RecordContext } from 'shared/records/RecordContext';
import { LineDescriptionAndQuantity } from 'shared/searchAndSelect/lines/LineDescriptionAndQuantity';
import { LineXComponent } from 'shared/searchAndSelect/lines/LineXComponent';
import { TextFieldSelectPatient } from 'shared/TextFieldSelectPatient';
import { IOrderLineModel } from './IOrderLineModel';
import { Typography16pxBold } from 'framework/components/typography/Typography16pxBold';

interface IProps extends IDraggableItem {
	line: IOrderLineModel;
	index: number;
	setLine: (val: IOrderLineModel) => void;
	onDelete: VoidFunction;
	onCopy: (count: number) => void;
}

export const RegisterOrderLineComponent = ({ line, setLine, onCopy, ...rest }: IProps) => {
	const strings = useLocalization();
	const { orientationRecord } = useContext(RecordContext);
	const localizedKeys = useLocalizedArticlePropertyKeys();
	const { open, confirm, cancel } = useDialogsContext();

	const onEditAps = () => {
		open(
			<ArticlePropertiesDialog
				open
				confirm={apLines => {
					confirm();
					setLine({ ...line, articleProperties: apLines });
				}}
				cancel={cancel}
				apds={line.productCatalogItem.articlePropertyDefinitions}
				aps={line.articleProperties}
			/>
		);
	};

	return (
		<LineXComponent
			{...rest}
			line={line}
			setLine={setLine}
			errors={line.error}>
			<LineDescriptionAndQuantity<IOrderLineModel>
				line={line}
				setLine={setLine}
				disableQuantity={line.productCatalogItem.isUniquelyIdentifiable}
			/>
			<div
				className='df-row-wrap gap-16'
				style={{ marginTop: 8 }}>
				<div className='df-col gap-16'>
					<div className='df-row-ac gap-4'>
						<TextFieldSelectPatient
							patientId={line.patientId}
							patientIdentifier={line.patientIdentifier}
							onPatientSelected={val => setLine({ ...line, patientId: val.id, patientIdentifier: val.identifier })}
							isOptional={line.productCatalogItem.isTailorMade === false}
						/>
						<IconButton
							size='small'
							color='primary'
							disabled={isNullOrUndefined(line.patientId)}
							onClick={() => setLine({ ...line, patientId: undefined, patientIdentifier: '' })}>
							<PersonAddDisabledOutlinedIcon />
						</IconButton>
					</div>
					{line.productCatalogItem.isUniquelyIdentifiable && (
						<SelectFieldFromRecord<Orientation>
							variant='outlined'
							size='small'
							label={strings.orientation}
							record={orientationRecord}
							value={line.orientation as any}
							onChange={value => setLine({ ...line, orientation: value })}
							withNone
						/>
					)}
				</div>
				{line.productCatalogItem.isUniquelyIdentifiable && line.productCatalogItem.articlePropertyDefinitions.length > 0 && (
					<>
						<Divider orientation='vertical' />
						<div className='df-col'>
							<CollapseX
								expandText={strings.showProperties}
								collapseText={strings.hideProperties}
								expandByDefault>
								<ArticlePropertiesLabelledPropertiesX
									aps={line.articleProperties}
									localizedKeys={localizedKeys}
									edit={onEditAps}
								/>
							</CollapseX>
						</div>
					</>
				)}
			</div>
			{line.productCatalogItem !== undefined && line.productCatalogItem.isUniquelyIdentifiable && (
				<div className='df-col gap-8'>
					<Divider style={{ marginTop: 8 }} />
					<div className='df-row-ac gap-16'>
						<TextLinkButton
							startIcon={<FileCopyOutlinedIcon />}
							onClick={() => onCopy(1)}
							color='primary'>
							{strings.copyThisLine}
						</TextLinkButton>
						<PopupState variant='popover'>
							{state => (
								<>
									<TextLinkButton
										startIcon={<FileCopyOutlinedIcon />}
										color='primary'
										{...bindTrigger(state)}>
										{strings.createXCopies(`X`)}
									</TextLinkButton>
									<Popover {...bindPopover(state)}>
										<CreateXCopiesStack
											onClose={state.close}
											onCopy={val => {
												onCopy(val);
												state.close();
											}}
										/>
									</Popover>
								</>
							)}
						</PopupState>
					</div>
				</div>
			)}
		</LineXComponent>
	);
};

type CreateXCopiesStackProps = {
	onCopy: (count: number) => void;
	onClose: VoidFunction;
};

export const CreateXCopiesStack = ({ onCopy }: CreateXCopiesStackProps) => {
	const [state, setState] = useState<number>(1);
	const strings = useLocalization();

	return (
		<div
			className='df-col gap-16'
			style={{ padding: 16 }}>
			<Typography16pxBold>{strings.createXCopies('X')}</Typography16pxBold>
			<Divider />
			<IntegerTextField
				value={state}
				onChange={val => setState(val ?? 1)}
				label={strings.numberOfCopies}
				// style={{ marginRight: theme.spacing(1.5) }}
			/>
			<Divider />
			<div className='df-row-ac'>
				<div className='fg1'></div>
				<Button
					variant='outlined'
					color='primary'
					onClick={() => onCopy(state)}>
					{strings.createXCopies(state)}
				</Button>
			</div>
		</div>
	);
};
